import * as stateActions from '../actions/stateActions';
import * as requestActions from '../actions/requestActions';
import * as sessionStates from '../sessionStates';
import { logSipEvent } from '../logger';
import { setupPeerConnectionDebug } from './peerConnectionUtils';
import { playAudio } from './audioUtils';
import { extractUserIdentity } from './sessionHandlers';

/**
 * Setup peer connection handlers
 */
export const setupPeerConnectionHandlers = (
	session,
	callId,
	store,
	logger,
	audioElement,
	applySavedMuteState,
	handleLocalTrackAdded,
	captureLocalStreamDirectly
) => {
	session.on('peerconnection', (data) => {
		const peerConnection = data.peerconnection;
		logSipEvent('peerconnection', data);

		// Log current SDP state
		if (peerConnection.localDescription) {
			console.group('Local SDP:');
			console.log(peerConnection.localDescription.sdp);
			console.groupEnd();
		}

		if (peerConnection.remoteDescription) {
			console.group('Remote SDP:');
			console.log(peerConnection.remoteDescription.sdp);
			console.groupEnd();
		}

		// Set up debugging for the peer connection
		setupPeerConnectionDebug(peerConnection);

		// Add error handling for connection failures
		peerConnection.addEventListener('connectionstatechange', () => {
			console.log('Connection state changed:', peerConnection.connectionState);

			if (peerConnection.connectionState === 'failed') {
				store.dispatch(requestActions.notify({
					type: 'error',
					text: 'WebRTC connection failed. Please check your network.'
				}));
			} else if (peerConnection.connectionState === 'disconnected') {
				store.dispatch(requestActions.notify({
					type: 'warning',
					text: 'Call connection lost. Attempting to reconnect...'
				}));
			}
		});

		// Handle remote tracks
		peerConnection.addEventListener('track', (event) => {
			try {
				const { userIdentity, userUri } = extractUserIdentity(session);

				console.log('Track received via track event', event.track);

				// Get existing remote stream or create a new one
				let remoteStream = store.getState().sessions[callId]?.remoteStream;

				if (!remoteStream) {
					remoteStream = new MediaStream();
					
					console.group('🎬 CONFERENCE CALL - USER JOINED WITH MEDIA');
					console.log('📞 Session ID (Call-ID):', callId);
					console.log('👤 User Name:', userIdentity);
					console.log('📧 User URI:', userUri);
					console.log('📅 Timestamp:', new Date().toISOString());
					console.groupEnd();
				}

				// Add the track to the remote stream
				remoteStream.addTrack(event.track);

				// Log track information with user details
				console.group(`📹 CONFERENCE CALL - ${event.track.kind.toUpperCase()} TRACK ADDED`);
				console.log('📞 Session ID (Call-ID):', callId);
				console.log('👤 User Name:', userIdentity);
				console.log('📧 User URI:', userUri);
				console.log('🎵 Track Type:', event.track.kind);
				console.log('🆔 Track ID:', event.track.id);
				console.log('✅ Track Enabled:', event.track.enabled);
				console.log('📊 Track State:', event.track.readyState);
				console.log('🔇 Track Muted:', event.track.muted);
				console.log('📈 Stream now has:', {
					audioTracks: remoteStream.getAudioTracks().length,
					videoTracks: remoteStream.getVideoTracks().length
				});
				console.log('📅 Timestamp:', new Date().toISOString());
				console.groupEnd();

				// If it's an audio track, set up audio playback
				if (event.track.kind === 'audio') {
					if (audioElement) {
						console.log('Connecting audio track to audio element');

						playAudio(audioElement, remoteStream)
							.then(success => {
								console.log('Audio playback result:', success ? 'success' : 'needs user interaction');

								store.dispatch(stateActions.setSessionState({
									sipSession: session,
									sessionState: sessionStates.ESTABLISHED
								}));
								
								const { userIdentity: ui, userUri: uu } = extractUserIdentity(session);
								
								console.group('🔊 CONFERENCE CALL - USER FULLY JOINED (AUDIO PLAYING)');
								console.log('📞 Session ID (Call-ID):', callId);
								console.log('👤 User Name:', ui);
								console.log('📧 User URI:', uu);
								console.log('📅 Timestamp:', new Date().toISOString());
								console.groupEnd();

								if (!success) {
									store.dispatch(requestActions.notify({
										type: 'warning',
										text: 'Click anywhere to enable audio'
									}));
								}
							})
							.catch(error => {
								console.error('Failed to play audio:', error);

								store.dispatch(stateActions.setSessionState({
									sipSession: session,
									sessionState: sessionStates.ESTABLISHED
								}));

								store.dispatch(requestActions.notify({
									type: 'warning',
									text: 'Audio playback issue. Click anywhere to enable audio.'
								}));
							});
					}
				} else if (event.track.kind === 'video') {
					console.log('Video track received, adding to remote stream');
				}

				// Set remote stream in store to update UI
				store.dispatch(stateActions.addRemoteStream({
					sipSession: session,
					remoteStream
				}));
			} catch (error) {
				logger.error('Failed to handle track event:', error);
				store.dispatch(requestActions.notify({
					type: 'error',
					text: `Media error: ${error.message}`
				}));
			}
		});

		peerConnection.addEventListener('iceconnectionstatechange', () => {
			console.log('ICE connection state:', peerConnection.iceConnectionState);
			if (peerConnection.iceConnectionState === 'failed') {
				console.error('ICE connection failed - check STUN/TURN servers');
				store.dispatch(requestActions.notify({
					type: 'error',
					text: 'ICE connection failed. Media cannot flow, check your network firewall settings.'
				}));
			} else if (peerConnection.iceConnectionState === 'disconnected') {
				store.dispatch(requestActions.notify({
					type: 'warning',
					text: 'ICE connection disconnected. Attempting to reconnect...'
				}));
			}
		});

		// Additional direct track event listener to catch local tracks as soon as they're added
		if (typeof peerConnection.onaddtrack !== 'undefined') {
			peerConnection.onaddtrack = (event) => {
				console.log('Local track added to peer connection:', event.track);
				handleLocalTrackAdded(session, event.track);
			};
		} else {
			peerConnection.addEventListener('track', (event) => {
				if (event.track && !event.streams) {
					console.log('Track added without stream (likely local):', event.track);
					handleLocalTrackAdded(session, event.track);
				}
			});
		}

		// Monitor for any sender changes which indicate local tracks were added
		const originalAddTrack = peerConnection.addTrack;
		peerConnection.addTrack = function () {
			const sender = originalAddTrack.apply(this, arguments);
			console.log('addTrack called with track:', arguments[0]);
			return sender;
		};

		// Enhanced local stream capture with multiple methods and fallbacks
		setTimeout(() => {
			try {
				console.log('Attempting to capture local stream from peer connection...');
				let localStream = null;
				let captureMethod = 'unknown';

				// Method 1: Try getLocalStreams() method
				if (peerConnection.getLocalStreams && peerConnection.getLocalStreams().length > 0) {
					localStream = peerConnection.getLocalStreams()[0];
					captureMethod = 'getLocalStreams';
				}
				// Method 2: Try getSenders() method for newer WebRTC implementations
				else if (peerConnection.getSenders) {
					const senders = peerConnection.getSenders();
					if (senders && senders.length > 0) {
						const tracks = senders.map(sender => sender.track).filter(track => track !== null);
						if (tracks.length > 0) {
							localStream = new MediaStream(tracks);
							captureMethod = 'getSenders';
						}
					}
				}

				// Method 3: Try to access the local stream directly from the session
				if (!localStream && session) {
					try {
						if (session._localMediaStream) {
							localStream = session._localMediaStream;
							captureMethod = 'session._localMediaStream';
						} else if (session.localMediaStream) {
							localStream = session.localMediaStream;
							captureMethod = 'session.localMediaStream';
						} else if (session._connection && session._connection.getLocalStreams && session._connection.getLocalStreams().length > 0) {
							localStream = session._connection.getLocalStreams()[0];
							captureMethod = 'session._connection.getLocalStreams';
						}
					} catch (e) {
						console.warn('Error accessing local media stream from session:', e);
					}
				}

				// If we found a local stream, store it
				if (localStream && (localStream.getVideoTracks().length > 0 || localStream.getAudioTracks().length > 0)) {
					console.log(`Found local stream with tracks using method ${captureMethod}:`, {
						video: localStream.getVideoTracks().length,
						audio: localStream.getAudioTracks().length
					});

					store.dispatch(stateActions.addLocalStream({
						sipSession: session,
						localStream
					}));
					applySavedMuteState(session, localStream);
				} else {
					console.warn('No local media stream found in peer connection - attempting to get user media directly');
					captureLocalStreamDirectly(session);
				}
			} catch (error) {
				console.error('Error during local stream capture:', error);
				captureLocalStreamDirectly(session);
			}
		}, 100);
	});
};

